import { FC } from 'react';
import { parse } from 'date-fns';
import { trim } from 'lodash-es';
import { Box, BoxProps, Link, Text } from '@chakra-ui/react';

import { DateTimeDisplayMode } from '~/core/shared/components/DateTimeDisplay/constants/DateTimeDisplayMode';
import { formatDateTime } from '~/core/shared/components/DateTimeDisplay/logic/formatDateTime';
import { DATE_FORMATS } from '~/core/shared/constants';
import {
  SummaryContentComponentElement,
  SummaryFormat,
} from '~/core/shared/types';
import { sanitizePrecision } from '~/core/shared/utils/general';
import { Check } from './elements/Check.component';
import { StateCheck } from './elements/StateCheck.component';
import { formatCategoryValue } from './logic/formatCategoryValue';

export type ContentElementProps = BoxProps & {
  element: SummaryContentComponentElement;
  hideName?: boolean;
};

const convertDate = (value: string): string => {
  try {
    const replacedIsoString = value.replace(
      /T\d{2}:\d{2}:\d{2}(\.\d{6})?/,
      'T00:00:000000',
    );
    return formatDateTime(
      parse(replacedIsoString, DATE_FORMATS.keys, new Date()),
      DateTimeDisplayMode.Date,
    );
  } catch (err) {
    // do nothing
  }
  try {
    const datePart = value.split('T')[0];
    return formatDateTime(new Date(datePart), DateTimeDisplayMode.Date);
  } catch (err) {
    // do nothing
  }
  return '';
};

export const ContentElement: FC<ContentElementProps> = ({
  element,
  hideName,
  ...props
}) => {
  let { w } = props;
  let { value } = element;
  const defaultValue = '-';

  if (element.format === SummaryFormat.Boolean) {
    const boolValue = value as unknown as boolean;
    value = boolValue ? 'Yes' : 'No';
  }

  // Value conversions
  if (value !== null) {
    if (
      element.format === SummaryFormat.Currency &&
      !Number.isNaN(parseInt(value, 10))
    ) {
      const formatter = new Intl.NumberFormat('en-US', {
        currency: 'USD',
        style: 'currency',
      });
      value = formatter.format(sanitizePrecision(parseInt(value, 10) / 100));
    }

    if (element.format === SummaryFormat.Number) {
      value = `${value}`;
    }

    if (element.format === SummaryFormat.Percent) {
      const numberValue = parseFloat((+value * 100).toPrecision(4));
      value = `${numberValue}%`;
    }

    if (element.format === SummaryFormat.Date) {
      value = value ? convertDate(value) : '';
    }

    if (element.format === SummaryFormat.Category) {
      value = formatCategoryValue(value, element.labels);
    }

    if (element.format === 'string') {
      if (element.key === SummaryFormat.Email) {
        value = `${value}`;
      } else {
        // Uppercase first
        value = `${value}`.charAt(0).toUpperCase() + `${value}`.slice(1);
      }
    }
  }

  value = value ? trim(value) : value;

  let node = (
    <>
      {!hideName && (
        <Text color="gray.700" fontSize="12px" textTransform="uppercase">
          {element.name}
        </Text>
      )}

      {(element.format === SummaryFormat.Link && value && (
        <Link href={value} target="blank_">
          Open Report
        </Link>
      )) || (
        <Text
          style={element.newLineText ? { whiteSpace: 'pre-line' } : undefined}
        >
          {value || defaultValue}
        </Text>
      )}
    </>
  );

  if (element.format === SummaryFormat.FullWidth) {
    w = '100%';
  }

  if (element.format === SummaryFormat.Check) {
    node = <Check element={element} />;
  }

  if (element.format === SummaryFormat.StateCheck) {
    node = <StateCheck element={element} />;
  }

  return (
    <Box {...props} w={w}>
      {node}
    </Box>
  );
};
